import { EditorHostError } from "./errors.ts";
import { EDITOR_DOCUMENT_KINDS, type EditorDocumentRef } from "./types.ts";

const KINDS = new Set<string>(EDITOR_DOCUMENT_KINDS);
const UNSAFE_SRC_PROTOCOLS = new Set(["file:", "javascript:", "data:"]);

export function normalizeDocumentRefs(
  doc: EditorDocumentRef | EditorDocumentRef[],
): EditorDocumentRef[] {
  const list = Array.isArray(doc) ? doc : [doc];
  if (list.length === 0) {
    throw new EditorHostError("invalid_document_ref", "loadDocument requires at least one document");
  }
  return list.map((item, index) => assertEditorDocumentRef(item, index));
}

export function assertEditorDocumentRef(
  value: unknown,
  index = 0,
): EditorDocumentRef {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new EditorHostError("invalid_document_ref", `document[${index}] must be an object`);
  }
  const raw = value as Record<string, unknown>;
  if (typeof raw.path !== "string") {
    throw new EditorHostError("invalid_document_ref", `document[${index}].path is required`);
  }
  const path = assertRelativeDocumentPath(raw.path, `document[${index}].path`);
  if (typeof raw.kind !== "string" || !KINDS.has(raw.kind)) {
    throw new EditorHostError(
      "invalid_document_ref",
      `document[${index}].kind must be schematic | board | project`,
      { kind: raw.kind },
    );
  }
  if (typeof raw.src !== "string") {
    throw new EditorHostError(
      "invalid_document_ref",
      `document[${index}].src is required (short-TTL URL or inline; host does not fetch)`,
    );
  }
  const src = assertDocumentSrc(raw.src, `document[${index}].src`);
  if (raw.name !== undefined && typeof raw.name !== "string") {
    throw new EditorHostError("invalid_document_ref", `document[${index}].name must be a string`);
  }
  return {
    path,
    kind: raw.kind as EditorDocumentRef["kind"],
    src,
    ...(raw.name === undefined ? {} : { name: raw.name.trim() }),
  };
}

/** Repo-relative POSIX path. Rejects absolute, traversal, and URL-shaped values. */
export function assertRelativeDocumentPath(path: string, label: string): string {
  const trimmed = path.trim();
  if (trimmed === "") {
    throw new EditorHostError("invalid_document_ref", `${label} is required`);
  }
  if (trimmed.includes("\0") || trimmed.includes("\\")) {
    throw new EditorHostError("invalid_document_ref", `${label} must be a POSIX path`);
  }
  if (trimmed.includes("://") || trimmed.startsWith("/") || /^[A-Za-z]:/.test(trimmed) || trimmed.startsWith("~")) {
    throw new EditorHostError("invalid_document_ref", `${label} must be repo-relative`);
  }
  const parts = trimmed.split("/");
  if (parts.some((part) => part === "" || part === "." || part === "..")) {
    throw new EditorHostError("invalid_document_ref", `${label} must not contain empty, '.', or '..' segments`);
  }
  return trimmed;
}

function assertDocumentSrc(src: string, label: string): string {
  const trimmed = src.trim();
  if (trimmed === "") {
    throw new EditorHostError(
      "invalid_document_ref",
      `${label} is required (short-TTL URL or inline; host does not fetch)`,
    );
  }
  if (/^[A-Za-z][A-Za-z0-9+.-]*:/.test(trimmed)) {
    let parsed: URL;
    try {
      parsed = new URL(trimmed);
    } catch {
      throw new EditorHostError("invalid_document_ref", `${label} is not a usable URL`);
    }
    const protocol = parsed.protocol.toLowerCase();
    if (UNSAFE_SRC_PROTOCOLS.has(protocol)) {
      throw new EditorHostError("invalid_document_ref", `${label} uses a blocked URL scheme`);
    }
    if (parsed.username || parsed.password) {
      throw new EditorHostError("invalid_document_ref", `${label} must not include userinfo`);
    }
  }
  return trimmed;
}
